import React, { useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useParams,
  useLocation,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import Home from "../page/Home";
import News from "../page/News";
import Recruit from "../page/Recruit";
import ScientificCareer from "../page/ScientificCareer";
import Development from "../page/Development";
import Business from "../page/Business";
import Service from "../page/Service";
import Company from "../page/Company";
import Inquiry from "../page/Inquiry";
import Faq from "../page/Faq";
import ColumnLayout, {
  ColumnArticlePage,
  ColumnCategoriesPage,
  ColumnListPage,
} from "../page/Column";
import NotFound from "../page/NotFound";
import {
  PATH_TO_LANG,
  isValidLangPath,
  DEFAULT_LANG_PATH,
  stripLangPrefix,
} from "../i18n/routing";

/**
 * 既知のトップレベルルート（旧URL互換用）
 */
const KNOWN_TOP_ROUTES = new Set([
  "company",
  "business",
  "service",
  "development",
  "scientific_career",
  "recruit",
  "news",
  "inquiry",
  "faq",
]);

/**
 * 方式2: ルート（/）アクセス時はリダイレクトせず、直接日本語版トップを表示する
 */
const RootJapaneseHome: React.FC = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (i18n.language !== "ja") {
      i18n.changeLanguage("ja");
    }
  }, [i18n]);

  return <Home />;
};

/**
 * コラム（/column）は多言語非対応のためプレフィックスなしで直接日本語表示
 */
const ColumnJapaneseLayout: React.FC = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (i18n.language !== "ja") {
      i18n.changeLanguage("ja");
    }
  }, [i18n]);

  return <ColumnLayout />;
};

/**
 * /:lang/column/* へのアクセスを正規の /column/* にリダイレクト
 */
const ColumnPrefixRedirect: React.FC = () => {
  const location = useLocation();
  const cleanPath = stripLangPrefix(location.pathname);
  return (
    <Navigate
      to={`${cleanPath}${location.search}${location.hash}`}
      replace
    />
  );
};

/**
 * 言語プレフィックス（/jp, /us, /cn）レイアウト
 */
const LanguageLayout: React.FC = () => {
  const { lang } = useParams<{ lang?: string }>();
  const location = useLocation();
  const { i18n } = useTranslation();

  const isKnownRoute = Boolean(lang && KNOWN_TOP_ROUTES.has(lang));
  const isValidLang = isValidLangPath(lang);
  const targetLang = isValidLang ? PATH_TO_LANG[lang] : undefined;

  useEffect(() => {
    if (targetLang && i18n.language !== targetLang) {
      i18n.changeLanguage(targetLang);
    }
  }, [targetLang, i18n]);

  // 旧URL（例: /company）が :lang として捕捉された場合、デフォルト言語（jp）へ転送
  if (isKnownRoute) {
    return (
      <Navigate
        to={`/${DEFAULT_LANG_PATH}${location.pathname}${location.search}${location.hash}`}
        replace
      />
    );
  }

  // 不正な言語プレフィックスの場合は 404
  if (!isValidLang) {
    return <NotFound />;
  }

  return <Outlet />;
};

/**
 * 階層が深い旧URLの互換リダイレクト
 */
const LegacyPathRedirect: React.FC = () => {
  const location = useLocation();
  const firstSegment = location.pathname.split("/").filter(Boolean)[0];

  if (firstSegment && KNOWN_TOP_ROUTES.has(firstSegment)) {
    return (
      <Navigate
        to={`/${DEFAULT_LANG_PATH}${location.pathname}${location.search}${location.hash}`}
        replace
      />
    );
  }

  return <NotFound />;
};

const AppContent: React.FC = () => {
  return (
    <div>
      <div className="site-layout-content">
        <Routes>
          {/* 方式2: ルート（/）はリダイレクトせず原地で日本語トップを表示 */}
          <Route path="/" element={<RootJapaneseHome />} />

          {/* コラムはプレフィックスなし */}
          <Route path="/column" element={<ColumnJapaneseLayout />}>
            <Route index element={<ColumnListPage />} />
            <Route path="categories" element={<ColumnCategoriesPage />} />
            <Route path=":articleId" element={<ColumnArticlePage />} />
          </Route>

          {/* /:lang/column/* はプレフィックスなしの /column/* へ転送 */}
          <Route path="/:lang/column/*" element={<ColumnPrefixRedirect />} />

          {/* 言語プレフィックス（/jp, /us, /cn）配下のルート */}
          <Route path="/:lang" element={<LanguageLayout />}>
            <Route index element={<Home />} />
            <Route path="company" element={<Company />} />
            <Route path="business" element={<Business />} />
            <Route path="service" element={<Service />} />
            <Route path="development" element={<Development />} />
            <Route path="scientific_career" element={<ScientificCareer />} />
            <Route path="recruit" element={<Recruit />} />
            <Route path="news" element={<News />} />
            <Route path="inquiry" element={<Inquiry />} />
            <Route path="faq" element={<Faq />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* 旧URLの互換フォールバックおよびその他404 */}
          <Route path="*" element={<LegacyPathRedirect />} />
        </Routes>
      </div>
    </div>
  );
};

export default AppContent;
